/* Named cockpit layout views — save / load / delete / rename.
   Views are independent of the "working" layout stored under
   `supplylens-cockpit-layout-v1`. Loading a view copies its layout
   into the working key so the RESET button keeps its semantics. */

import { useCallback, useEffect, useMemo, useState } from "react"
import type { Layout } from "react-grid-layout/legacy"
import { DEFAULT_LAYOUT } from "./layout"

export interface SavedView {
  id: string
  name: string
  layout: Layout
  createdAt: number
  builtin?: boolean
}

const VIEWS_STORAGE_KEY = "supplylens-cockpit-views-v1"
const ACTIVE_VIEW_STORAGE_KEY = "supplylens-cockpit-active-view-v1"

/* Builtin presets are regenerated on every load so they can't drift if the
   panel-id schema changes across releases. User-saved views take the shape
   they were saved with. */
export const BUILTIN_DEFAULT_ID = "builtin:default"
const BUILTIN_COMPACT_ID = "builtin:compact"
const BUILTIN_FOCUS_GLOBE_ID = "builtin:focus-globe"

/* Compact preset: everything tighter, globe smaller. */
const COMPACT_LAYOUT: Layout = [
  { i: "streams", x: 0, y: 0, w: 6, h: 5 },
  { i: "fleet", x: 6, y: 0, w: 3, h: 5 },
  { i: "risk", x: 9, y: 0, w: 3, h: 3 },
  { i: "market", x: 9, y: 3, w: 3, h: 2 },
  { i: "lanes", x: 0, y: 5, w: 4, h: 7 },
  { i: "intel", x: 4, y: 5, w: 5, h: 7 },
  { i: "signals", x: 9, y: 5, w: 3, h: 4 },
  { i: "escalation", x: 9, y: 9, w: 3, h: 3 },
  { i: "infra", x: 0, y: 12, w: 4, h: 4 },
  { i: "economic", x: 4, y: 12, w: 8, h: 2 },
  { i: "globe", x: 0, y: 16, w: 12, h: 10 },
]

/* Focus: Globe — the 3D globe dominates, everything else is a strip on the side. */
const FOCUS_GLOBE_LAYOUT: Layout = [
  { i: "globe", x: 0, y: 0, w: 8, h: 22 },
  { i: "risk", x: 8, y: 0, w: 4, h: 3 },
  { i: "fleet", x: 8, y: 3, w: 4, h: 4 },
  { i: "intel", x: 8, y: 7, w: 4, h: 8 },
  { i: "signals", x: 8, y: 15, w: 4, h: 4 },
  { i: "escalation", x: 8, y: 19, w: 4, h: 3 },
  { i: "streams", x: 0, y: 22, w: 6, h: 5 },
  { i: "lanes", x: 6, y: 22, w: 3, h: 5 },
  { i: "market", x: 9, y: 22, w: 3, h: 5 },
  { i: "infra", x: 0, y: 27, w: 6, h: 4 },
  { i: "economic", x: 6, y: 27, w: 6, h: 4 },
]

function freshBuiltins(): SavedView[] {
  const now = Date.now()
  return [
    {
      id: BUILTIN_DEFAULT_ID,
      name: "Default",
      layout: DEFAULT_LAYOUT,
      createdAt: now,
      builtin: true,
    },
    {
      id: BUILTIN_COMPACT_ID,
      name: "Compact",
      layout: COMPACT_LAYOUT,
      createdAt: now,
      builtin: true,
    },
    {
      id: BUILTIN_FOCUS_GLOBE_ID,
      name: "Focus: Globe",
      layout: FOCUS_GLOBE_LAYOUT,
      createdAt: now,
      builtin: true,
    },
  ]
}

function isLayout(value: unknown): value is Layout {
  if (!Array.isArray(value)) return false
  return value.every(
    (item) =>
      typeof item === "object" &&
      item !== null &&
      typeof (item as { i?: unknown }).i === "string" &&
      typeof (item as { x?: unknown }).x === "number" &&
      typeof (item as { y?: unknown }).y === "number" &&
      typeof (item as { w?: unknown }).w === "number" &&
      typeof (item as { h?: unknown }).h === "number",
  )
}

function isSavedView(value: unknown): value is SavedView {
  if (typeof value !== "object" || value === null) return false
  const v = value as Record<string, unknown>
  return (
    typeof v.id === "string" &&
    typeof v.name === "string" &&
    typeof v.createdAt === "number" &&
    isLayout(v.layout)
  )
}

function readStoredViews(): SavedView[] | null {
  if (typeof window === "undefined") return null
  try {
    const raw = window.localStorage.getItem(VIEWS_STORAGE_KEY)
    if (!raw) return null
    const parsed = JSON.parse(raw) as unknown
    if (!Array.isArray(parsed)) return null
    const filtered = parsed.filter(isSavedView)
    return filtered
  } catch {
    return null
  }
}

function writeStoredViews(views: SavedView[]) {
  if (typeof window === "undefined") return
  try {
    window.localStorage.setItem(VIEWS_STORAGE_KEY, JSON.stringify(views))
  } catch {
    // Private mode / quota — silent fail, matches layout-v1 behaviour.
  }
}

function readStoredActiveViewId(): string | null {
  if (typeof window === "undefined") return null
  try {
    return window.localStorage.getItem(ACTIVE_VIEW_STORAGE_KEY)
  } catch {
    return null
  }
}

function writeStoredActiveViewId(id: string | null) {
  if (typeof window === "undefined") return
  try {
    if (id === null) window.localStorage.removeItem(ACTIVE_VIEW_STORAGE_KEY)
    else window.localStorage.setItem(ACTIVE_VIEW_STORAGE_KEY, id)
  } catch {
    // ignore
  }
}

/* Merge builtins with any user-saved views found in storage. Builtins always
   appear first in a stable order; user views preserve their saved order. */
function mergeWithBuiltins(stored: SavedView[] | null): SavedView[] {
  const builtins = freshBuiltins()
  if (!stored) return builtins
  const userViews = stored.filter((v) => !v.builtin)
  return [...builtins, ...userViews]
}

/* Fired (on window) whenever saved views change outside the hook — e.g. the
   LLM `save_view` chat action persisting a view from the zustand store. Every
   mounted useViews() instance re-reads storage when it sees this. */
export const VIEWS_CHANGED_EVENT = "supplylens:views-changed"

/**
 * Persist a named view directly to localStorage (used by the chat
 * `save_view` action, which runs outside any React component). Marks the new
 * view active and notifies mounted useViews() instances.
 */
export function saveViewToStorage(name: string, layout: Layout): SavedView | null {
  const trimmed = name.trim()
  if (!trimmed) return null
  const newView: SavedView = {
    id: `view:${Date.now().toString(36)}:${Math.random().toString(36).slice(2, 8)}`,
    name: trimmed,
    layout: layout.map((item) => ({ ...item })),
    createdAt: Date.now(),
  }
  const existing = (readStoredViews() ?? []).filter((v) => !v.builtin)
  writeStoredViews([...existing, newView])
  writeStoredActiveViewId(newView.id)
  if (typeof window !== "undefined") {
    window.dispatchEvent(new Event(VIEWS_CHANGED_EVENT))
  }
  return newView
}

export interface UseViewsReturn {
  views: SavedView[]
  activeViewId: string | null
  saveView: (name: string, layout: Layout) => SavedView | null
  loadView: (id: string) => SavedView | null
  deleteView: (id: string) => void
  renameView: (id: string, name: string) => void
  setActiveViewId: (id: string | null) => void
}

export function useViews(): UseViewsReturn {
  const [views, setViews] = useState<SavedView[]>(() => mergeWithBuiltins(readStoredViews()))
  const [activeViewId, setActiveViewIdState] = useState<string | null>(() => {
    const stored = readStoredActiveViewId()
    return stored ?? BUILTIN_DEFAULT_ID
  })

  // Persist user-authored views (we never persist builtins — they're regenerated on load).
  useEffect(() => {
    const userViews = views.filter((v) => !v.builtin)
    writeStoredViews(userViews)
  }, [views])

  useEffect(() => {
    writeStoredActiveViewId(activeViewId)
  }, [activeViewId])

  // Re-read storage when views change outside this hook instance (LLM
  // save_view action, another mounted instance).
  useEffect(() => {
    const onChanged = () => {
      setViews(mergeWithBuiltins(readStoredViews()))
      const storedActive = readStoredActiveViewId()
      if (storedActive) setActiveViewIdState(storedActive)
    }
    window.addEventListener(VIEWS_CHANGED_EVENT, onChanged)
    return () => window.removeEventListener(VIEWS_CHANGED_EVENT, onChanged)
  }, [])

  const setActiveViewId = useCallback((id: string | null) => {
    setActiveViewIdState(id)
  }, [])

  const saveView = useCallback((name: string, layout: Layout): SavedView | null => {
    const trimmed = name.trim()
    if (!trimmed) return null
    const newView: SavedView = {
      id: `view:${Date.now().toString(36)}:${Math.random().toString(36).slice(2, 8)}`,
      name: trimmed,
      // Clone to detach from the caller's reference — layouts get mutated by the grid.
      layout: layout.map((item) => ({ ...item })),
      createdAt: Date.now(),
    }
    setViews((prev) => [...prev, newView])
    setActiveViewIdState(newView.id)
    return newView
  }, [])

  const loadView = useCallback(
    (id: string): SavedView | null => {
      const view = views.find((v) => v.id === id)
      if (!view) return null
      setActiveViewIdState(id)
      return view
    },
    [views],
  )

  const deleteView = useCallback((id: string) => {
    setViews((prev) => {
      const target = prev.find((v) => v.id === id)
      if (!target || target.builtin) return prev
      return prev.filter((v) => v.id !== id)
    })
    // If the active view was deleted, fall back to Default.
    setActiveViewIdState((prev) => (prev === id ? BUILTIN_DEFAULT_ID : prev))
  }, [])

  const renameView = useCallback((id: string, name: string) => {
    const trimmed = name.trim()
    if (!trimmed) return
    setViews((prev) =>
      prev.map((v) => {
        if (v.id !== id) return v
        if (v.builtin) return v // Builtins are immutable.
        return { ...v, name: trimmed }
      }),
    )
  }, [])

  return useMemo(
    () => ({
      views,
      activeViewId,
      saveView,
      loadView,
      deleteView,
      renameView,
      setActiveViewId,
    }),
    [views, activeViewId, saveView, loadView, deleteView, renameView, setActiveViewId],
  )
}
